import { liveQuery } from "dexie";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { ReceivingPanel } from "./ReceivingPanel";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  employeeSession: LocalEmployeeSessionState | null;
};

export function ReceivingRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, employeeSession: null });
  const [online, setOnline] = useState(navigator.onLine);
  const [mountNode, setMountNode] = useState<Element | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      employeeSession: await readLocalEmployeeSession(),
    })).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, employeeSession: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateNetwork = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
    };
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-header__actions"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const terminal = runtime.terminal;
  const session = runtime.employeeSession;
  const actor = session?.actor ?? null;
  const identity = terminal?.identity ?? null;

  const sameScope = Boolean(
    actor && identity
      && actor.organization_id === identity.organization_id
      && actor.branch_id === identity.branch_id
      && actor.node_id === identity.node_id
      && actor.runtime_session_status === "ACTIVE",
  );

  if (!mountNode || !terminal || !actor || !sameScope) return null;

  return createPortal(
    <ReceivingPanel actor={actor} terminal={terminal} online={online} />,
    mountNode,
  );
}
